// Quantità netta e composizione della confezione (Reg. UE 1169/2011, art. 23
// e Allegato IX). Gemello di backend/net_quantity.py, che ricalcola tutto lato
// server: qui serve per l'anteprima immediata e i messaggi di errore.
// Calcoli in numeri interi (millesimi di grammo/millilitro): 20 × 0,1 kg = 2 kg esatti.

export const UNITS = { g: 1000, kg: 1000000, ml: 1000, l: 1000000 };
export const MASS_UNITS = ["g", "kg"];
export const SINGLE_UNITS = ["g", "kg", "ml", "l"];
export const MAX_PIECES = 9999;
const MAX_TOTAL = 1000 * UNITS.kg;
export const PACK_MODES = [
  { key: "single", label: "Prodotto singolo", hint: "Inserisci solo la quantità netta." },
  { key: "fixed", label: "Multipla a peso fisso", hint: "Pezzi × peso unitario: il totale si calcola da solo." },
  { key: "variable", label: "Multipla a peso variabile", hint: "Pezzi + totale effettivamente pesato." },
];

const DECIMAL = /^\s*(\d{1,7})(?:[.,](\d{1,3}))?\s*$/;

/** "0,1" + "kg" -> 100000 (millesimi di grammo). null se non valido. */
export function parseAmount(value, unit) {
  if (!UNITS[unit]) return null;
  const match = DECIMAL.exec(String(value ?? ""));
  if (!match) return null;
  const frac = (match[2] || "").padEnd(3, "0");
  return Number(match[1]) * UNITS[unit] + (Number(frac) * UNITS[unit]) / 1000;
}

function fmtNumber(milli, unit) {
  const factor = UNITS[unit];
  const whole = Math.floor(milli / factor);
  const rest = milli % factor;
  if (!rest) return String(whole);
  return `${whole},${String(rest).padStart(String(factor).length - 1, "0").replace(/0+$/, "")}`;
}

/** Senza unità imposta: kg (o l) da 1000 in su se esprimibile con massimo 3 decimali, altrimenti g (o ml). */
export function formatAmount(milli, unit, volume = false) {
  const [small, big] = volume ? ["ml", "l"] : ["g", "kg"];
  const u = unit === small || unit === big ? unit : (milli >= UNITS[big] && milli % 1000 === 0 ? big : small);
  return `${fmtNumber(milli, u)} ${u}`;
}

/**
 * Risultato per tutte le modalità: { net, composition, error }.
 * Prodotto singolo: net dal valore + unità (vuoto se non inserito).
 */
export function computePack(data) {
  const mode = data.packMode || "single";
  if (mode === "single") {
    const unit = SINGLE_UNITS.includes(data.packTotalUnit) ? data.packTotalUnit : "g";
    const text = String(data.packTotalValue ?? "").trim();
    if (!text) return { net: "", composition: "", error: null };
    const total = parseAmount(text, unit);
    if (!total) return { net: "", composition: "", error: "Quantità netta: inserisci un valore maggiore di zero (es. 150 oppure 1,5 — massimo 3 decimali)." };
    if (total > MAX_TOTAL) return { net: "", composition: "", error: "Quantità netta oltre 1000 kg: controlla il valore inserito." };
    return { net: `${fmtNumber(total, unit)} ${unit}`, composition: "", error: null };
  }
  const piecesText = String(data.packPieces ?? "").trim();
  if (!/^\d{1,4}$/.test(piecesText) || Number(piecesText) < 1) {
    return { net: "", composition: "", error: `Numero di pezzi: inserisci un numero intero da 1 a ${MAX_PIECES}.` };
  }
  const pieces = Number(piecesText);
  const totalUnit = MASS_UNITS.includes(data.packTotalUnit) ? data.packTotalUnit : null;
  if (mode === "fixed") {
    const unit = MASS_UNITS.includes(data.packPieceUnit) ? data.packPieceUnit : "g";
    const piece = parseAmount(data.packPieceValue, unit);
    if (!piece) return { net: "", composition: "", error: "Peso netto del singolo pezzo: inserisci un valore maggiore di zero (es. 100 g oppure 0,1 kg, massimo 3 decimali)." };
    const total = piece * pieces;
    if (total > MAX_TOTAL) return { net: "", composition: "", error: "Quantità totale oltre 1000 kg: controlla pezzi e peso unitario." };
    return { net: formatAmount(total, totalUnit), composition: pieces > 1 ? `${pieces} pezzi da ${formatAmount(piece)} ciascuno` : "", error: null };
  }
  const unit = totalUnit || "g";
  const total = parseAmount(data.packTotalValue, unit);
  if (!total) return { net: "", composition: "", error: "Quantità netta complessiva rilevata: inserisci un valore maggiore di zero (es. 2,35 kg)." };
  if (total > MAX_TOTAL) return { net: "", composition: "", error: "Quantità totale oltre 1000 kg: controlla il valore inserito." };
  return { net: `${fmtNumber(total, unit)} ${unit}`, composition: pieces > 1 ? `${pieces} pezzi a peso variabile` : "", error: null };
}

/** Applica un cambiamento ai campi della confezione e ricalcola netQuantity e packComposition. */
export function withPack(data, patch) {
  const next = { ...data, ...patch };
  const { net, composition } = computePack(next);
  return { ...next, netQuantity: net, packComposition: composition };
}

/**
 * Etichette salvate prima di questa sezione: solo il testo netQuantity.
 * Se è "numero + unità" diventa prodotto singolo strutturato; altrimenti il
 * testo resta com'è (legacy) finché l'operatore non inserisce un valore.
 */
export function upgradeLegacyPack(data) {
  if (data.packMode) return data;
  const match = /^\s*(\d{1,7}(?:[.,]\d{1,3})?)\s*(g|kg|ml|l)\s*$/i.exec(data.netQuantity || "");
  if (!match) return { ...data, packMode: "single" };
  return { ...data, packMode: "single", packTotalValue: match[1], packTotalUnit: match[2].toLowerCase() };
}

export const netQuantityLine = (net, composition) => composition ? `Quantità netta totale: ${net} – ${composition}` : `Quantità netta: ${net}`;
